/**
 * Tournaments — Bracket-style competitions.
 *
 * Supports: single elimination, double elimination, round robin, swiss.
 * Bracket generation, seeding by Elo, match progression.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';
import { getEloRating, processDuelResult } from './elo';
import { isRateLimited } from './rateLimiter';

const log = createLogger('TOURNAMENTS');

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export interface Tournament {
  id: string;
  name: string;
  description: string | null;
  format: 'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss';
  bracket_size: number;
  status: string;
  weight_class_id: string | null;
  age_division_id: string | null;
  lift_category: string | null;
  starts_at: string;
  ends_at: string | null;
  entry_fee_cents: number;
  prize_pool_cents: number;
}

export interface TournamentMatch {
  id: string;
  round_number: number;
  match_number: number;
  player_a_id: string | null;
  player_b_id: string | null;
  winner_id: string | null;
  status: string;
  deadline_at: string | null;
}

// ────────────────────────────────────────────
// Tournament CRUD
// ────────────────────────────────────────────

export async function fetchTournaments(status?: string): Promise<Tournament[]> {
  let query = supabase
    .from('tournaments')
    .select('*')
    .order('starts_at', { ascending: true });

  if (status) query = query.eq('status', status);

  const { data, error } = await query;
  if (error) {
    log.error('fetchTournaments failed:', error);
    return [];
  }
  return data ?? [];
}

export async function fetchTournament(id: string): Promise<Tournament | null> {
  const { data } = await supabase
    .from('tournaments')
    .select('*')
    .eq('id', id)
    .single();
  return data;
}

export async function registerForTournament(
  tournamentId: string,
  profileId: string,
): Promise<boolean> {
  if (isRateLimited('tournament_register', 10, 3_600_000)) {
    throw new Error('Too many requests. Please wait a moment.');
  }
  const elo = await getEloRating(profileId);

  const { error } = await supabase
    .from('tournament_participants')
    .upsert({
      tournament_id: tournamentId,
      profile_id: profileId,
      elo_at_entry: elo.rating,
      status: 'registered',
    }, { onConflict: 'tournament_id,profile_id', ignoreDuplicates: true });

  if (error) {
    log.error('registerForTournament failed:', error);
    return false;
  }
  return true;
}

export async function createTournament(params: {
  name: string;
  description?: string;
  format: Tournament['format'];
  bracket_size: number;
  lift_category?: string;
  starts_at: string;
  entry_fee_cents?: number;
  prize_pool_cents?: number;
  gym_id?: string;
  custom_rules?: string;
}): Promise<string | null> {
  const { data, error } = await supabase
    .from('tournaments')
    .insert({
      name: params.name,
      description: params.description || null,
      format: params.format,
      bracket_size: params.bracket_size,
      lift_category: params.lift_category || null,
      starts_at: params.starts_at,
      entry_fee_cents: params.entry_fee_cents || 0,
      prize_pool_cents: params.prize_pool_cents || 0,
      status: 'registration',
      ...(params.gym_id ? { gym_id: params.gym_id } : {}),
      ...(params.custom_rules ? { custom_rules: params.custom_rules } : {}),
    })
    .select('id')
    .single();

  if (error) {
    log.error('createTournament failed:', error);
    return null;
  }
  return data.id;
}

// ────────────────────────────────────────────
// Bracket Generation
// ────────────────────────────────────────────

/**
 * Generate a single-elimination bracket.
 * Seeds participants by Elo (highest rated = seed 1).
 * Handles byes for non-power-of-2 participant counts.
 */
export async function generateBracket(tournamentId: string): Promise<boolean> {
  // Fetch participants
  const { data: participants } = await supabase
    .from('tournament_participants')
    .select('profile_id, elo_at_entry')
    .eq('tournament_id', tournamentId)
    .eq('status', 'registered')
    .order('elo_at_entry', { ascending: false });

  if (!participants || participants.length < 2) {
    log.error('Not enough participants to generate bracket');
    return false;
  }

  // Get tournament bracket size
  const tournament = await fetchTournament(tournamentId);
  if (!tournament) return false;

  const bracketSize = tournament.bracket_size;
  const numRounds = Math.log2(bracketSize);

  // Seed participants
  const seeded = participants.slice(0, bracketSize);
  for (let i = 0; i < seeded.length; i++) {
    await supabase
      .from('tournament_participants')
      .update({ seed_number: i + 1, status: 'active' })
      .eq('tournament_id', tournamentId)
      .eq('profile_id', seeded[i].profile_id);
  }

  // Generate first-round matchups using standard bracket seeding
  // Seed 1 vs Seed N, Seed 2 vs Seed N-1, etc.
  const firstRoundMatches = bracketSize / 2;
  const matches: any[] = [];

  for (let i = 0; i < firstRoundMatches; i++) {
    const seedA = i + 1;
    const seedB = bracketSize - i;
    const playerA = seeded[seedA - 1]?.profile_id || null;
    const playerB = seeded[seedB - 1]?.profile_id || null;

    const isBye = !playerA || !playerB;

    matches.push({
      tournament_id: tournamentId,
      round_number: 1,
      match_number: i + 1,
      player_a_id: playerA,
      player_b_id: playerB,
      status: isBye ? 'bye' : 'pending',
      winner_id: isBye ? (playerA || playerB) : null,
    });
  }

  // Generate empty matches for subsequent rounds
  let matchesInRound = firstRoundMatches / 2;
  for (let round = 2; round <= numRounds; round++) {
    for (let m = 0; m < matchesInRound; m++) {
      matches.push({
        tournament_id: tournamentId,
        round_number: round,
        match_number: m + 1,
        player_a_id: null,
        player_b_id: null,
        status: 'pending',
      });
    }
    matchesInRound = matchesInRound / 2;
  }

  // Insert all matches
  const { error } = await supabase
    .from('tournament_matches')
    .insert(matches);

  if (error) {
    log.error('generateBracket insert failed:', error);
    return false;
  }

  // Update tournament status
  await supabase
    .from('tournaments')
    .update({ status: 'in_progress' })
    .eq('id', tournamentId);

  log.info('Bracket generated', {
    tournamentId,
    participants: seeded.length,
    totalMatches: matches.length,
    rounds: numRounds,
  });

  return true;
}

// ────────────────────────────────────────────
// Match Resolution
// ────────────────────────────────────────────

/**
 * Submit a match result and advance the winner.
 */
export async function resolveMatch(
  matchId: string,
  winnerId: string,
  playerAScore?: Record<string, number>,
  playerBScore?: Record<string, number>,
): Promise<boolean> {
  const { data: match } = await supabase
    .from('tournament_matches')
    .select('*')
    .eq('id', matchId)
    .single();

  if (!match || match.status === 'completed') return false;

  const loserId = match.player_a_id === winnerId ? match.player_b_id : match.player_a_id;

  // Update match
  await supabase
    .from('tournament_matches')
    .update({
      winner_id: winnerId,
      status: 'completed',
      player_a_score: playerAScore,
      player_b_score: playerBScore,
      completed_at: new Date().toISOString(),
    })
    .eq('id', matchId);

  // Update Elo
  if (loserId) {
    await processDuelResult(winnerId, loserId, 'overall', match.tournament_id);
  }

  // Advance winner to next round
  if (match.next_match_id) {
    const { data: nextMatch } = await supabase
      .from('tournament_matches')
      .select('player_a_id, player_b_id')
      .eq('id', match.next_match_id)
      .single();

    if (nextMatch) {
      const update = !nextMatch.player_a_id
        ? { player_a_id: winnerId }
        : { player_b_id: winnerId };

      await supabase
        .from('tournament_matches')
        .update(update)
        .eq('id', match.next_match_id);
    }
  }

  // Mark loser as eliminated
  if (loserId) {
    await supabase
      .from('tournament_participants')
      .update({ status: 'eliminated' })
      .eq('tournament_id', match.tournament_id)
      .eq('profile_id', loserId);
  }

  // Check if this was the final match
  const { count: remaining } = await supabase
    .from('tournament_matches')
    .select('id', { count: 'exact', head: true })
    .eq('tournament_id', match.tournament_id)
    .in('status', ['pending', 'active']);

  if ((remaining ?? 0) === 0) {
    // Tournament complete
    await supabase
      .from('tournaments')
      .update({ status: 'completed' })
      .eq('id', match.tournament_id);

    await supabase
      .from('tournament_participants')
      .update({ status: 'winner', final_placement: 1 })
      .eq('tournament_id', match.tournament_id)
      .eq('profile_id', winnerId);

    log.info('Tournament completed', { tournamentId: match.tournament_id, winnerId });
  }

  return true;
}

/**
 * Get the bracket view for a tournament (all matches organized by round).
 */
export async function getBracketView(tournamentId: string): Promise<Map<number, TournamentMatch[]>> {
  const { data } = await supabase
    .from('tournament_matches')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('round_number')
    .order('match_number');

  const bracket = new Map<number, TournamentMatch[]>();
  for (const match of data ?? []) {
    const round = match.round_number;
    if (!bracket.has(round)) bracket.set(round, []);
    bracket.get(round)!.push(match);
  }
  return bracket;
}
